import { Select } from 'antd'
import { Moment } from 'moment'
import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ButtonContainer,
  ButtonExport,
  ButtonGroup,
  ButtonGroupWrapper,
  DatePickerCustom,
  NoteEmployee,
  SearchWrapper,
  SelectWrapper,
  TitleSearchEmployee,
} from '@/components/pages/AttendanceEmployee/styles'
import { Icon, RangeDateWrapper } from '@/components/pages/ClientDevice/styles'
import { dateTime } from '@/configs'
import { defaultFilterDate, FilterDateEmployee } from '@/pages/AttendanceEmployee/AttendanceEmployeeByDay'
import { DateType } from '@/types'
import {
  ExportAttendanceEmployeeByDayRequest,
  ExportAttendanceEmployeeByRangeDayRequest,
  ExportAttendanceTotalEmployeeByRangeDayRequest,
  GetAttendanceEmployeeByDayRequest,
  GetAttendanceEmployeeByRangeDayRequest,
  GetAttendanceTotalEmployeeByRangeDayRequest,
} from '@/types/apis/attendanceEmployee'
import { GetAllEmployeesRequest, GetAllEmployeesResponse, GetValueSearchEmployeeResponse } from '@/types/apis/employees'
import { formatDateSendRequest } from '@/utils/date'
import { getName } from '@/utils/employee'

type Props = {
  getAttendanceEmployeeByDay: (request: GetAttendanceEmployeeByDayRequest) => Promise<void>
  exportExcelAttendanceEmployeeByDay: (request: ExportAttendanceEmployeeByDayRequest) => Promise<void>
  exportEmployeeLoading: boolean
  getEmployeeByRangeDay: (request: GetAttendanceEmployeeByRangeDayRequest) => Promise<void>
  exportExcelEmployeeByRangeDate: (request: ExportAttendanceEmployeeByRangeDayRequest) => Promise<void>
  exportEmployeeByRangeDayLoading: boolean
  getTotalEmployeeByRangeDay: (request: GetAttendanceTotalEmployeeByRangeDayRequest) => Promise<void>
  exportExcelTotalEmployeeByRangeDay: (request: ExportAttendanceTotalEmployeeByRangeDayRequest) => Promise<void>
  exportTotalEmployeeLoading: boolean
  getAllEmployees: (request: GetAllEmployeesRequest) => Promise<void>
  allEmployeesResponse?: GetAllEmployeesResponse
  setDateFilter: React.Dispatch<React.SetStateAction<Moment | null | undefined>>
  dateFilter?: Moment | null
  setDateRangeFilter: React.Dispatch<React.SetStateAction<FilterDateEmployee>>
  dateRangeFilter: FilterDateEmployee
  getValueSearchEmployee: () => Promise<void>
  valueSearchEmployee?: GetValueSearchEmployeeResponse
  employeeFilter: boolean
  setEmployeeFilter: React.Dispatch<React.SetStateAction<boolean>>
}

type SearchEmployee = {
  gender?: string
  position?: string
  department?: string
}

const searchEmployeeDefault = {
  gender: '-1',
  position: '-1',
  department: '-1',
}

const FilterAttendanceEmployeeByDay: FC<Props> = ({
  getAttendanceEmployeeByDay,
  exportExcelAttendanceEmployeeByDay,
  exportEmployeeLoading,
  getEmployeeByRangeDay,
  exportExcelEmployeeByRangeDate,
  exportEmployeeByRangeDayLoading,
  getTotalEmployeeByRangeDay,
  exportExcelTotalEmployeeByRangeDay,
  exportTotalEmployeeLoading,
  getAllEmployees,
  allEmployeesResponse,
  setDateRangeFilter,
  dateRangeFilter,
  setDateFilter,
  dateFilter,
  getValueSearchEmployee,
  valueSearchEmployee,
  employeeFilter,
  setEmployeeFilter,
}) => {
  const { t } = useTranslation()
  const [employeeId, setEmployeeId] = useState<number>()
  const [searchEmployee, setSearchEmployee] = useState<SearchEmployee>(searchEmployeeDefault)

  const defaultSelectEmployee = {
    value: '-1',
    label: t('components.pages.attendanceEmployee.listReport.filter.defaultValue'),
  }

  const optionSelectEmployee = allEmployeesResponse?.map((employee) => {
    return {
      value: employee.id.toString(),
      label: getName(employee),
    }
  })

  const optionSearchGender = valueSearchEmployee?.gender.map((item) => {
    return {
      label: item,
      value: item,
    }
  })

  const optionSearchPosition = valueSearchEmployee?.position.map((item) => {
    return {
      label: item,
      value: item,
    }
  })

  const optionSearchDepartment = valueSearchEmployee?.department.map((item) => {
    return {
      label: item,
      value: item,
    }
  })

  const defaultOptionSearchGender = optionSearchGender && [defaultSelectEmployee, ...optionSearchGender]
  const defaultOptionSearchOption = optionSearchPosition && [defaultSelectEmployee, ...optionSearchPosition]
  const defaultOptionSearchDepartment = optionSearchDepartment && [defaultSelectEmployee, ...optionSearchDepartment]
  const defaultSelectEmployees = optionSelectEmployee && [defaultSelectEmployee, ...optionSelectEmployee]

  const handleSelectEmployee = (value: string, type: DateType) => {
    setEmployeeId(Number(value) ? Number(value) : undefined)
    if (type === DateType.BY_DAY && dateFilter) {
      getAttendanceEmployeeByDay({
        date: formatDateSendRequest(dateFilter),
        employeeId: Number(value) !== Number(defaultSelectEmployee.value) ? Number(value) : undefined,
      })
    }
    if (type === DateType.RANGE_DAY) {
      dateRangeFilter.fromDate &&
        dateRangeFilter.toDate &&
        getEmployeeByRangeDay({
          fromDate: formatDateSendRequest(dateRangeFilter.fromDate),
          toDate: formatDateSendRequest(dateRangeFilter.toDate),
          employeeId: Number(value) !== Number(defaultSelectEmployee.value) ? Number(value) : undefined,
        })
    }
    if (type === DateType.TOTAL_RANGE_DAY) {
      dateRangeFilter.totalFromDate &&
        dateRangeFilter.totalToDate &&
        getTotalEmployeeByRangeDay({
          fromDate: formatDateSendRequest(dateRangeFilter.totalFromDate),
          toDate: formatDateSendRequest(dateRangeFilter.totalToDate),
          employeeId: Number(value) !== Number(defaultSelectEmployee.value) ? Number(value) : undefined,
        })
    }
  }

  const handleSelectSearchEmployee = (value: string, type: string) => {
    switch (type) {
      case 'gender':
        setSearchEmployee((pre) => ({ ...pre, gender: value }))
        break
      case 'position':
        setSearchEmployee((pre) => ({ ...pre, position: value }))
        break
      case 'department':
        setSearchEmployee((pre) => ({ ...pre, department: value }))
        break
      default:
        break
    }
  }

  const handlePickByDay = (date: Moment | null) => {
    setDateRangeFilter((pre) => ({ ...pre, type: DateType.BY_DAY }))
    setDateFilter(date)
    getAttendanceEmployeeByDay({
      date: formatDateSendRequest(date),
      employeeId: employeeId !== Number(defaultSelectEmployee.value) ? employeeId : undefined,
      gender: searchEmployee.gender !== '-1' ? searchEmployee.gender : undefined,
      department: searchEmployee.department !== '-1' ? searchEmployee.department : undefined,
      position: searchEmployee.position !== '-1' ? searchEmployee.position : undefined,
    })
  }

  const handlePickRangeDay = (type: string, date: Moment | null) => {
    switch (type) {
      case 'fromByTotalDate':
        setDateRangeFilter((pre) => ({ ...pre, type: DateType.TOTAL_RANGE_DAY, totalFromDate: date }))
        date &&
          dateRangeFilter.totalToDate &&
          getTotalEmployeeByRangeDay({
            fromDate: formatDateSendRequest(date),
            toDate: formatDateSendRequest(dateRangeFilter.totalToDate),
            employeeId: employeeId !== Number(defaultSelectEmployee.value) ? employeeId : undefined,
            gender: searchEmployee.gender !== '-1' ? searchEmployee.gender : undefined,
            department: searchEmployee.department !== '-1' ? searchEmployee.department : undefined,
            position: searchEmployee.position !== '-1' ? searchEmployee.position : undefined,
          })
        break

      case 'toByTotalDate':
        setDateRangeFilter((pre) => ({
          ...pre,
          type: DateType.TOTAL_RANGE_DAY,
          totalToDate: date,
        }))
        getTotalEmployeeByRangeDay({
          fromDate: formatDateSendRequest(dateRangeFilter.totalFromDate),
          toDate: formatDateSendRequest(date),
          employeeId: employeeId !== Number(defaultSelectEmployee.value) ? employeeId : undefined,
          gender: searchEmployee.gender !== '-1' ? searchEmployee.gender : undefined,
          department: searchEmployee.department !== '-1' ? searchEmployee.department : undefined,
          position: searchEmployee.position !== '-1' ? searchEmployee.position : undefined,
        })
        break

      case 'fromByRangeDate':
        setDateRangeFilter((pre) => ({ ...pre, type: DateType.RANGE_DAY, fromDate: date }))
        date &&
          dateRangeFilter.toDate &&
          getEmployeeByRangeDay({
            fromDate: formatDateSendRequest(date),
            toDate: formatDateSendRequest(dateRangeFilter.toDate),
            employeeId: employeeId !== Number(defaultSelectEmployee.value) ? employeeId : undefined,
            gender: searchEmployee.gender !== '-1' ? searchEmployee.gender : undefined,
            department: searchEmployee.department !== '-1' ? searchEmployee.department : undefined,
            position: searchEmployee.position !== '-1' ? searchEmployee.position : undefined,
          })
        break

      case 'toByRangeDate':
        setDateRangeFilter((pre) => ({ ...pre, type: DateType.RANGE_DAY, toDate: date }))
        getEmployeeByRangeDay({
          fromDate: formatDateSendRequest(dateRangeFilter.fromDate),
          toDate: formatDateSendRequest(date),
          employeeId: employeeId !== Number(defaultSelectEmployee.value) ? employeeId : undefined,
          gender: searchEmployee.gender !== '-1' ? searchEmployee.gender : undefined,
          department: searchEmployee.department !== '-1' ? searchEmployee.department : undefined,
          position: searchEmployee.position !== '-1' ? searchEmployee.position : undefined,
        })
        break

      default:
        break
    }
  }

  const handleExportCSV = () => {
    if (dateFilter) {
      exportExcelAttendanceEmployeeByDay({
        date: formatDateSendRequest(dateFilter),
        employeeId: employeeId !== Number(defaultSelectEmployee.value) ? employeeId : undefined,
        gender: searchEmployee.gender !== '-1' ? searchEmployee.gender : undefined,
        department: searchEmployee.department !== '-1' ? searchEmployee.department : undefined,
        position: searchEmployee.position !== '-1' ? searchEmployee.position : undefined,
        sort: employeeFilter ? 'DESC' : 'ASC',
      })
    }
  }

  const handleExportCSVEmployeeByRangeDay = () => {
    if (dateRangeFilter.fromDate && dateRangeFilter.toDate) {
      exportExcelEmployeeByRangeDate({
        fromDate: formatDateSendRequest(dateRangeFilter.fromDate),
        toDate: formatDateSendRequest(dateRangeFilter.toDate),
        employeeId: employeeId !== Number(defaultSelectEmployee.value) ? employeeId : undefined,
        gender: searchEmployee.gender !== '-1' ? searchEmployee.gender : undefined,
        department: searchEmployee.department !== '-1' ? searchEmployee.department : undefined,
        position: searchEmployee.position !== '-1' ? searchEmployee.position : undefined,
        sort: employeeFilter ? 'DESC' : 'ASC',
      })
    }
  }

  const handleExportCSVTotalEmployee = () => {
    if (dateRangeFilter.totalFromDate && dateRangeFilter.totalToDate) {
      exportExcelTotalEmployeeByRangeDay({
        fromDate: formatDateSendRequest(dateRangeFilter.totalFromDate),
        toDate: formatDateSendRequest(dateRangeFilter.totalToDate),
        employeeId: employeeId !== Number(defaultSelectEmployee.value) ? employeeId : undefined,
        gender: searchEmployee.gender !== '-1' ? searchEmployee.gender : undefined,
        department: searchEmployee.department !== '-1' ? searchEmployee.department : undefined,
        position: searchEmployee.position !== '-1' ? searchEmployee.position : undefined,
        sort: employeeFilter ? 'DESC' : 'ASC',
      })
    }
  }

  const handleDownLoadCSV = (type: DateType) => {
    switch (type) {
      case 1:
        handleExportCSV()
        break
      case 2:
        handleExportCSVEmployeeByRangeDay()
        break
      case 3:
        handleExportCSVTotalEmployee()
        break
      default:
        break
    }
  }

  const handleReset = () => {
    setDateFilter(null)
    setDateRangeFilter(defaultFilterDate)
    setEmployeeId(-1)
    setSearchEmployee(searchEmployeeDefault)
    setEmployeeFilter(false)
  }

  useEffect(() => {
    getAllEmployees({
      gender: searchEmployee.gender !== defaultSelectEmployee.value ? searchEmployee.gender : '',
      position: searchEmployee.position !== defaultSelectEmployee.value ? searchEmployee.position : '',
      department: searchEmployee.department !== defaultSelectEmployee.value ? searchEmployee.department : '',
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAllEmployees, searchEmployee])

  useEffect(() => {
    if (!employeeId || employeeId === -1) {
      if (dateFilter) {
        getAttendanceEmployeeByDay({
          date: formatDateSendRequest(dateFilter),
          employeeId: employeeId !== Number(defaultSelectEmployee.value) ? employeeId : undefined,
          gender: searchEmployee.gender !== '-1' ? searchEmployee.gender : undefined,
          department: searchEmployee.department !== '-1' ? searchEmployee.department : undefined,
          position: searchEmployee.position !== '-1' ? searchEmployee.position : undefined,
          sort: employeeFilter ? 'DESC' : 'ASC',
        })
      }
      if (dateRangeFilter.fromDate && dateRangeFilter.toDate) {
        getEmployeeByRangeDay({
          fromDate: formatDateSendRequest(dateRangeFilter.fromDate),
          toDate: formatDateSendRequest(dateRangeFilter.toDate),
          employeeId: employeeId !== Number(defaultSelectEmployee.value) ? employeeId : undefined,
          gender: searchEmployee.gender !== '-1' ? searchEmployee.gender : undefined,
          department: searchEmployee.department !== '-1' ? searchEmployee.department : undefined,
          position: searchEmployee.position !== '-1' ? searchEmployee.position : undefined,
          sort: employeeFilter ? 'DESC' : 'ASC',
        })
      }
      if (dateRangeFilter.totalFromDate && dateRangeFilter.totalToDate) {
        getTotalEmployeeByRangeDay({
          fromDate: formatDateSendRequest(dateRangeFilter.totalFromDate),
          toDate: formatDateSendRequest(dateRangeFilter.totalToDate),
          employeeId: employeeId !== Number(defaultSelectEmployee.value) ? employeeId : undefined,
          gender: searchEmployee.gender !== '-1' ? searchEmployee.gender : undefined,
          department: searchEmployee.department !== '-1' ? searchEmployee.department : undefined,
          position: searchEmployee.position !== '-1' ? searchEmployee.position : undefined,
          sort: employeeFilter ? 'DESC' : 'ASC',
        })
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getAttendanceEmployeeByDay,
    getEmployeeByRangeDay,
    getTotalEmployeeByRangeDay,
    searchEmployee,
    employeeId,
    employeeFilter,
  ])

  useEffect(() => {
    getValueSearchEmployee()
  }, [getValueSearchEmployee])

  return (
    <>
      <ButtonContainer>
        <ButtonGroup>
          <SearchWrapper>
            <TitleSearchEmployee>
              {t('components.pages.attendanceEmployee.listReport.filter.filterDay')}
            </TitleSearchEmployee>
            <DatePickerCustom
              value={dateFilter}
              format={dateTime.dateFormat}
              onChange={(date) => handlePickByDay(date)}
              disabled={dateRangeFilter.type !== DateType.BY_DAY && dateRangeFilter.type !== DateType.NO_SELECT}
            />
          </SearchWrapper>
          <SearchWrapper>
            <TitleSearchEmployee width="210px">
              {t('components.pages.attendanceEmployee.listReport.filter.filterByRangeDay')}
            </TitleSearchEmployee>
            <RangeDateWrapper>
              <DatePickerCustom
                value={dateRangeFilter.fromDate}
                onChange={(date) => handlePickRangeDay('fromByRangeDate', date)}
                format={dateTime.dateFormat}
                disabled={dateRangeFilter.type !== DateType.RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT}
              />
              <Icon>~</Icon>
              <DatePickerCustom
                value={dateRangeFilter.toDate}
                onChange={(date) => handlePickRangeDay('toByRangeDate', date)}
                format={dateTime.dateFormat}
                disabled={dateRangeFilter.type !== DateType.RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT}
              />
            </RangeDateWrapper>
          </SearchWrapper>

          <SearchWrapper>
            <TitleSearchEmployee width="210px">
              {t('components.pages.attendanceEmployee.listReport.filter.filterByTime')}
            </TitleSearchEmployee>
            <RangeDateWrapper>
              <DatePickerCustom
                value={dateRangeFilter.totalFromDate}
                onChange={(date) => handlePickRangeDay('fromByTotalDate', date)}
                format={dateTime.dateFormat}
                disabled={
                  dateRangeFilter.type !== DateType.TOTAL_RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT
                }
              />
              <Icon>~</Icon>
              <DatePickerCustom
                value={dateRangeFilter.totalToDate}
                onChange={(date) => handlePickRangeDay('toByTotalDate', date)}
                format={dateTime.dateFormat}
                disabled={
                  dateRangeFilter.type !== DateType.TOTAL_RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT
                }
              />
            </RangeDateWrapper>
          </SearchWrapper>
        </ButtonGroup>

        <ButtonGroup>
          <SelectWrapper>
            <TitleSearchEmployee width="50px">
              {t('components.pages.attendanceEmployee.listReport.filter.gender')}
            </TitleSearchEmployee>
            <Select
              showSearch
              style={{ width: 120 }}
              onChange={(value) => handleSelectSearchEmployee(value, 'gender')}
              options={defaultOptionSearchGender}
              value={searchEmployee.gender}
              defaultValue={defaultSelectEmployee.label}
            />
          </SelectWrapper>
          <SelectWrapper>
            <TitleSearchEmployee width="50px">
              {t('components.pages.attendanceEmployee.listReport.filter.position')}
            </TitleSearchEmployee>
            <Select
              showSearch
              style={{ width: 120 }}
              onChange={(value) => handleSelectSearchEmployee(value, 'position')}
              options={defaultOptionSearchOption}
              value={searchEmployee.position}
              defaultValue={defaultSelectEmployee.label}
            />
          </SelectWrapper>

          <SelectWrapper>
            <TitleSearchEmployee width="50px">
              {t('components.pages.attendanceEmployee.listReport.filter.department')}
            </TitleSearchEmployee>
            <Select
              showSearch
              style={{ width: 120 }}
              onChange={(value) => handleSelectSearchEmployee(value, 'department')}
              options={defaultOptionSearchDepartment}
              value={searchEmployee.department}
              defaultValue={defaultSelectEmployee.label}
            />
          </SelectWrapper>
        </ButtonGroup>

        <ButtonGroup>
          <SelectWrapper>
            <TitleSearchEmployee>
              {t('components.pages.attendanceEmployee.listReport.filter.filterEmployee')}
            </TitleSearchEmployee>
            <Select
              showSearch
              style={{ width: 200 }}
              onChange={(value) => handleSelectEmployee(value, dateRangeFilter.type)}
              options={defaultSelectEmployees}
              value={employeeId?.toString()}
              defaultValue={defaultSelectEmployee.label}
            />
          </SelectWrapper>
          <NoteEmployee>{t('components.pages.attendanceEmployee.listReport.filter.note')}</NoteEmployee>
          <ButtonGroupWrapper>
            <ButtonExport
              loading={exportEmployeeLoading || exportEmployeeByRangeDayLoading || exportTotalEmployeeLoading}
              disabled={dateRangeFilter.type === DateType.NO_SELECT}
              onClick={() => handleDownLoadCSV(dateRangeFilter.type)}
            >
              {t('components.pages.attendanceEmployee.listReport.table.export')}
            </ButtonExport>
            {dateRangeFilter.type !== DateType.NO_SELECT && (
              <ButtonExport danger onClick={handleReset}>
                {t('components.pages.clientDevice.listReport.table.reset')}
              </ButtonExport>
            )}
          </ButtonGroupWrapper>
        </ButtonGroup>
      </ButtonContainer>
    </>
  )
}

FilterAttendanceEmployeeByDay.displayName = 'FilterAttendanceEmployeeByDay'
export default FilterAttendanceEmployeeByDay
